import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService, BasicAuthStrategy, IdpAuthStrategy } from '@rsmu/auth';
import { Observable, Subject, of, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ConnectionService } from "./shared/services/connection.service";
import { filter, switchMap, takeUntil } from "rxjs/operators";
import { MatSnackBar } from "@angular/material/snack-bar";
import { OverlayContainer } from "@angular/cdk/overlay";
import { ActivatedRoute, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { RoutingUtilsService } from './shared/services/routing-utils.service';
import { MenuService } from './shared/services/menu.service';
import { MenuElementDto, ProfileDto } from '@rsmu/oep-api';
import { IomModuleService } from './shared/services/iom-module.service';
import { CurrentColorService } from './shared/services/curent-color.service';
import { DeviceWidthService } from './shared/services/width.service';
import { SideMenuColorEnum } from './shared/models/enums/side-menu-color.enum';
import { DeviceType } from './shared/models/interfaces/device-type.type';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { IconsService } from './core/services/icons.service';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { SideNavComponent } from './shared/components/side-nav/side-nav.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [MatSidenavModule, MatButtonModule, MatIconModule, SideNavComponent, MatToolbarModule, NgIf, RouterOutlet, AsyncPipe]
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'Парацельс';
  deviceType: DeviceType = 'desktop';
  isMenuOpen: boolean = false;
  menuItems: MenuElementDto[] = [];
  parentLink: string = '';
  currentColor: string | null = null;
  profile$!: Observable<ProfileDto>;

  private unsubscribe$: Subject<void> = new Subject<void>();

  get logo() {
    return environment.style === 'paracelsus' ?
      '/assets/icons/rsmu_logo (1).svg' : '/assets/icons/nmfo-logo.svg'
  }

  get moduleId(): string | undefined {
    return this.routingUtilsService.moduleId;
  };

  get showResetBtn():  boolean | undefined {
    return this.iom.showResetBtn;
  }

  @ViewChild('sideNav') sideNav!: MatSidenav;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.deviceWidth.setDeviceType(event.target.innerWidth);
  }

  ngOnInit(): void {
    this.deviceWidth.setDeviceType(window.innerWidth);

    this.deviceWidth.getDeviceType$()
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(type => {
      this.deviceType = type;
      if (this.deviceType === 'mobile') {
        this.closeSidenav();
      }
    });

    this.router.events
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(event => {
      if (event instanceof NavigationStart) {
        this.currentColorService.resetCurrentColor();
      }
    });
    this.menuService.menuItems$
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(items => {
      this.menuItems = items;
      const index = items.findIndex(item => {
        return item.current;
      });
      const colors = Object.values(SideMenuColorEnum) as string[];
      if (index !== -1 && index < colors.length) {

        this.currentColorService.setCurrentColor(colors[index])

      } else {
        this.currentColorService.setCurrentColor('#A1DFF5');
      }
    });

    this.menuService.parentLink$.subscribe(link => {
      this.parentLink = link;
    });

    this.auth.isAuthenticated$.pipe(
      takeUntil(this.unsubscribe$),
      filter(auth => auth === true),
      take(1),
      switchMap(() => this.iom.getProfile$())
    ).subscribe(profile => {
      this.profile$ = of(profile);
    });
  }

  eduModuleStringUrl(): string {
    if(!this.activatedRoute.parent) {
      return '';
    }
    return '/' + this.routingUtilsService.getStringUrl(this.activatedRoute.parent?.snapshot);
  }

  constructor(
    private readonly titleService: Title,
    private readonly auth: AuthService,
    private readonly connectionService: ConnectionService,
    private readonly overlatContainer: OverlayContainer,
    private readonly snackBar: MatSnackBar,
    private readonly activatedRoute: ActivatedRoute,
    private readonly routingUtilsService: RoutingUtilsService,
    private readonly router: Router,
    private readonly iom: IomModuleService,
    private menuService: MenuService,
    private currentColorService: CurrentColorService,
    private deviceWidth: DeviceWidthService,
    private readonly iconsService: IconsService,
  ) {

    this.titleService.setTitle(this.title);

    this.connectionService.offline$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.snackBar.open('Отсутствует связь с сервером. Проверьте соединение с интернетом.', '', {
          verticalPosition: 'top'
        });
      });
    this.connectionService.online$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.snackBar.dismiss();
      });

    if(environment.style === 'paracelsus') {
      this.overlatContainer.getContainerElement().classList.add('paracelsus-theme')
    }

    this.iconsService.initIcons();
    const strategy = environment.production ? IdpAuthStrategy : BasicAuthStrategy;
    this.auth.use(strategy);
  }

  logout() {
    this.auth.logout$()
    .pipe(take(1))
    .subscribe();
  }

  toggleSidenav(): void {
    this.sideNav.toggle();
  }

  closeSidenav(): void {
    if (this.sideNav) {
      this.sideNav.close();
    }
  }

  goHome() {
    this.router.navigate(['/', this.moduleId])
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
